'use client';

import React, { useState, useCallback, useMemo } from 'react';
import { motion } from 'framer-motion';
import type { Customer, ProfileUpdateData } from '@/services/CustomerApiService';
import { useCustomerPanelTranslations } from '@/lib/customer-panel-utils';
import { extractErrorMessage } from '@/lib/customer-panel/error-handler';
import { ErrorMessage, SuccessMessage, FormField, LoadingSpinner } from '@/lib/customer-panel/ui-components';
import { createFieldAriaAttributes } from '@/lib/customer-panel/accessibility';
import CountryCitySelect, { type Country } from '@/components/ui/CountryCitySelect';
import DatePickerCustom from '@/components/ui/DatePickerCustom';
import { parse, format } from 'date-fns';

interface PersonalInfoFormProps {
  customer: Customer;
  onUpdate: (data: ProfileUpdateData) => Promise<void>;
  translations?: Record<string, string>;
  countries: Country[];
}

export default function PersonalInfoForm({
  customer,
  onUpdate,
  translations = {},
  countries,
}: PersonalInfoFormProps) {
  const t = useCustomerPanelTranslations(translations);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const [formData, setFormData] = useState<ProfileUpdateData>({
    first_name: customer.first_name || '',
    last_name: customer.last_name || '',
    phone: customer.phone || '',
    birth_date: customer.birth_date || '',
    address: customer.address || '',
    city: customer.city || '',
    country: customer.country || '',
    postal_code: customer.postal_code || '',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      await onUpdate(formData);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError(extractErrorMessage(err, t('customer_panel.common.error', 'An error occurred')));
    } finally {
      setLoading(false);
    }
  };

  const handleChange = useCallback((field: keyof ProfileUpdateData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  }, []);

  const inputBaseClasses = "border border-[rgba(0,0,0,0.08)] focus:!border-[rgba(0,0,0,0.40)] transition-all ease-in-out duration-300 rounded-lg text-[#4F545A] font-medium text-sm sm:text-[15px] px-4 sm:px-6 py-3 sm:py-4 disabled:opacity-50 disabled:cursor-not-allowed w-full";

  return (
    <form onSubmit={handleSubmit} className="space-y-6" noValidate>
      {error && <ErrorMessage message={error} />}
      {success && <SuccessMessage message={t('customer_panel.common.update_success', 'Successfully updated')} />}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField
          id="first_name"
          label={t('customer_panel.profile.first_name', 'First Name')}
          required
          error={null}
        >
          <motion.input
            type="text"
            id="first_name"
            value={formData.first_name}
            onChange={(e) => handleChange('first_name', e.target.value)}
            required
            className={inputBaseClasses}
            whileHover={{ scale: 1.005, borderColor: "rgba(0, 0, 0, 0.15)" }}
            whileFocus={{ scale: 1.01, borderColor: "rgba(227, 30, 36, 0.3)" }}
            transition={{ type: "spring" as const, stiffness: 300, damping: 20 }}
            {...createFieldAriaAttributes('first_name', t('customer_panel.profile.first_name', 'First Name'), true)}
          />
        </FormField>

        <FormField
          id="last_name"
          label={t('customer_panel.profile.last_name', 'Last Name')}
          required
          error={null}
        >
          <motion.input
            type="text"
            id="last_name"
            value={formData.last_name}
            onChange={(e) => handleChange('last_name', e.target.value)}
            required
            className={inputBaseClasses}
            whileHover={{ scale: 1.005, borderColor: "rgba(0, 0, 0, 0.15)" }}
            whileFocus={{ scale: 1.01, borderColor: "rgba(227, 30, 36, 0.3)" }}
            transition={{ type: "spring" as const, stiffness: 300, damping: 20 }}
            {...createFieldAriaAttributes('last_name', t('customer_panel.profile.last_name', 'Last Name'), true)}
          />
        </FormField>

        <FormField
          id="phone"
          label={t('customer_panel.profile.phone', 'Phone')}
          error={null}
        >
          <motion.input
            type="tel"
            id="phone"
            value={formData.phone || ''}
            onChange={(e) => handleChange('phone', e.target.value)}
            className={inputBaseClasses}
            whileHover={{ scale: 1.005, borderColor: "rgba(0, 0, 0, 0.15)" }}
            whileFocus={{ scale: 1.01, borderColor: "rgba(227, 30, 36, 0.3)" }}
            transition={{ type: "spring" as const, stiffness: 300, damping: 20 }}
            {...createFieldAriaAttributes('phone', t('customer_panel.profile.phone', 'Phone'))}
          />
        </FormField>

        <div className="flex flex-col gap-2">
          <label className="font-semibold text-base sm:text-lg text-alsero-dark-gray">
            {t('customer_panel.profile.birth_date', 'Birth Date')}
          </label>
          <div className="border border-[rgba(0,0,0,0.08)] rounded-lg overflow-hidden">
            <DatePickerCustom
              label=""
              selected={formData.birth_date ? parse(formData.birth_date, 'yyyy-MM-dd', new Date()) : undefined}
              onSelect={(date) => handleChange('birth_date', date ? format(date, 'yyyy-MM-dd') : '')}
            />
          </div>
        </div>

        <FormField
          id="address"
          label={t('customer_panel.profile.address', 'Address')}
          error={null}
          className="md:col-span-2"
        >
          <motion.textarea
            id="address"
            value={formData.address || ''}
            onChange={(e) => handleChange('address', e.target.value)}
            rows={3}
            className={inputBaseClasses + " resize-y min-h-[120px]"}
            whileHover={{ scale: 1.005, borderColor: "rgba(0, 0, 0, 0.15)" }}
            whileFocus={{ scale: 1.01, borderColor: "rgba(227, 30, 36, 0.3)" }}
            transition={{ type: "spring" as const, stiffness: 300, damping: 20 }}
            {...createFieldAriaAttributes('address', t('customer_panel.profile.address', 'Address'))}
          />
        </FormField>

        <div className="md:col-span-2">
          <CountryCitySelect
            countries={countries}
            countryValue={formData.country || null}
            cityValue={formData.city || null}
            onCountryChange={(countryId, countryCode) => {
              handleChange('country', countryCode || '');
            }}
            onCityChange={(cityId, cityName) => {
              handleChange('city', cityName || '');
            }}
            countryPlaceholder={t('customer_panel.profile.select_country', 'Select Country')}
            cityPlaceholder={t('customer_panel.profile.select_city', 'Select City')}
          />
        </div>

        <FormField
          id="postal_code"
          label={t('customer_panel.profile.postal_code', 'Postal Code')}
          error={null}
        >
          <motion.input
            type="text"
            id="postal_code"
            value={formData.postal_code || ''}
            onChange={(e) => handleChange('postal_code', e.target.value)}
            className={inputBaseClasses}
            maxLength={20}
            whileHover={{ scale: 1.005, borderColor: "rgba(0, 0, 0, 0.15)" }}
            whileFocus={{ scale: 1.01, borderColor: "rgba(227, 30, 36, 0.3)" }}
            transition={{ type: "spring" as const, stiffness: 300, damping: 20 }}
            {...createFieldAriaAttributes('postal_code', t('customer_panel.profile.postal_code', 'Postal Code'))}
          />
        </FormField>
      </div>

      <div className="flex justify-end">
        <button
          type="submit"
          disabled={loading}
          className="px-6 py-2 bg-alsero-first text-white rounded-md hover:bg-alsero-second focus:outline-none focus:ring-2 focus:ring-alsero-first focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          aria-label={loading ? t('customer_panel.common.loading', 'Loading...') : t('customer_panel.common.save', 'Save')}
        >
          {loading ? (
            <>
              <LoadingSpinner size="sm" className="inline-block mr-2" />
              {t('customer_panel.common.loading', 'Loading...')}
            </>
          ) : (
            t('customer_panel.common.save', 'Save')
          )}
        </button>
      </div>
    </form>
  );
}
